import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Title1,
  Title3,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowClockwise20Regular,
  ArrowLeft24Regular,
  Edit20Regular,
  ErrorCircleRegular,
  LockClosed20Regular,
} from '@fluentui/react-icons';
import { useAuth } from '@/features/auth';
import { AgentAddressField } from '@/features/auth/components/AgentAddressField';
import type { CallerProfile } from '@/features/auth/api/contracts';
import { getCallerProfile, isApiFailure, saveAgent } from '@/features/auth/api/verificationApi';
import { messageForCode, verificationFailure } from '@/features/auth/api/errors';
import {
  REQUIRED_AGENT_FIELDS,
  AGENT_ADDRESS_FIELDS,
  EMPTY_AGENT_DETAILS,
  agentDetailsSchema,
  agentDetailsChanged,
  agentProfileFormSchema,
  formatPhoneInput,
  initialAgentDetails,
  phoneFormatFor,
  type AgentDetails,
  type RequiredAgentField,
} from '@/features/auth/registration';

const AGENT_LABELS: Record<RequiredAgentField, string> = {
  displayName: 'Full name',
  titleRank: 'Title or rank',
  role: 'Role',
  phone: 'Phone number',
};

const useStyles = makeStyles({
  page: {
    width: '100%',
    maxWidth: '1040px',
    boxSizing: 'border-box',
    marginLeft: 'auto',
    marginRight: 'auto',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXL,
  },
  heading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: tokens.spacingVerticalM,
  },
  muted: { color: tokens.colorNeutralForeground3 },
  title: { marginTop: 0, marginBottom: 0 },
  card: {
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
  },
  cardHeading: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
  },
  saveError: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: tokens.spacingVerticalS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  saved: {
    color: tokens.colorStatusSuccessForeground1,
  },
  errorCard: {
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    alignItems: 'center',
    rowGap: tokens.spacingVerticalXL,
  },
  errorContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalM,
    textAlign: 'center',
  },
  errorIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    fontSize: '32px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
  },
  errorDescription: {
    marginTop: 0,
    marginBottom: 0,
    maxWidth: '460px',
    color: tokens.colorNeutralForeground2,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    rowGap: tokens.spacingVerticalL,
    columnGap: tokens.spacingHorizontalL,
    minWidth: 0,
    marginTop: tokens.spacingVerticalL,
    '& > *': {
      alignSelf: 'start',
      minWidth: 0,
    },
    '& .fui-Input': {
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    },
    '@media (max-width: 700px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  fullWidthField: {
    gridColumn: '1 / -1',
  },
  readOnlyControl: {
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
    ':hover,:focus-within': {
      ...shorthands.borderColor(tokens.colorNeutralStroke2),
    },
    '& input': {
      cursor: 'default',
    },
  },
});

type ProfileState =
  | { status: 'loading' }
  | { status: 'loaded'; profile: CallerProfile }
  | { status: 'error'; message: string; unauthenticated: boolean };

function ProfileField({ label, value, identity = false, className }: {
  label: string;
  value?: string | null;
  identity?: boolean;
  className?: string;
}) {
  const styles = useStyles();
  return (
    <Field
      className={className}
      label={label}
      hint={identity ? 'Verified identity. This cannot be changed.' : undefined}
    >
      <Input
        appearance="filled-darker"
        className={styles.readOnlyControl}
        readOnly
        value={value?.trim() ?? ''}
        contentAfter={identity ? <LockClosed20Regular aria-hidden="true" /> : undefined}
      />
    </Field>
  );
}

export function ProfilePage({ onBack }: { onBack: () => void }) {
  const styles = useStyles();
  const { account, userProfile, logout } = useAuth();
  const [state, setState] = useState<ProfileState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [discardAction, setDiscardAction] = useState<'back' | 'cancel' | null>(null);
  const discardTrigger = useRef<HTMLButtonElement | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const editButton = useRef<HTMLButtonElement>(null);
  const saveVersion = useRef(0);
  const profile = state.status === 'loaded' ? state.profile : null;
  const agencyEmail = profile?.identity.identityEmail;
  const country = profile?.agency?.country ?? '';
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting: saving },
  } = useForm<AgentDetails>({
    defaultValues: EMPTY_AGENT_DETAILS,
    resolver: zodResolver(agentProfileFormSchema(country)),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const draft = watch();
  const saveError = errors.root?.server;

  useEffect(() => {
    heading.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    saveVersion.current += 1;
    setState({ status: 'loading' });
    setEditing(false);
    reset(EMPTY_AGENT_DETAILS);
    setSaved(false);
    setDiscardAction(null);
    void getCallerProfile().then(
      (profile) => {
        if (!cancelled) setState({ status: 'loaded', profile });
      },
      (error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: isApiFailure(error) ? error.message : messageForCode('Unknown'),
            unauthenticated: isApiFailure(error) &&
              (error.code === 'Unauthenticated' || error.status === 401),
          });
        }
      },
    );
    return () => {
      cancelled = true;
      saveVersion.current += 1;
    };
  }, [attempt, account?.homeAccountId, reset]);

  const dirty = editing && profile !== null &&
    agentDetailsChanged(draft, profile.agent, country);
  const phoneFormat = phoneFormatFor(draft.phone, country);

  function finishEditing() {
    setEditing(false);
    reset(EMPTY_AGENT_DETAILS);
    requestAnimationFrame(() => editButton.current?.focus());
  }

  function requestDiscard(action: 'back' | 'cancel', trigger: HTMLButtonElement) {
    if (saving) return;
    if (dirty) {
      discardTrigger.current = trigger;
      setDiscardAction(action);
    } else if (action === 'back') {
      onBack();
    } else {
      finishEditing();
    }
  }

  function keepEditing() {
    setDiscardAction(null);
    requestAnimationFrame(() => discardTrigger.current?.focus());
  }

  async function saveChanges(details: AgentDetails) {
    if (!editing || !profile || !dirty) return;
    const version = saveVersion.current;
    clearErrors('root.server');
    try {
      const agent = await saveAgent(details);
      if (!agentDetailsSchema.safeParse(agent).success) {
        throw verificationFailure('InvalidResponse', 200);
      }
      if (version !== saveVersion.current) return;
      setState({ status: 'loaded', profile: { ...profile, agent } });
      finishEditing();
      setSaved(true);
    } catch (error: unknown) {
      if (version !== saveVersion.current) return;
      setError('root.server', {
        message: isApiFailure(error) && error.code === 'RecordChanged'
          ? 'Your profile changed before it could be saved. Try again to save your changes.'
          : isApiFailure(error) ? error.message : messageForCode('Unknown'),
        type: isApiFailure(error) &&
          (error.code === 'Unauthenticated' || error.status === 401) ? 'Unauthenticated' : 'server',
      });
    }
  }

  return (
    <section className={styles.page} aria-labelledby="profile-page-title">
      <div className={styles.heading}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          disabled={saving}
          onClick={(event) => requestDiscard('back', event.currentTarget)}
        >
          Back to portal
        </Button>
        <h1 className={styles.title} id="profile-page-title" tabIndex={-1} ref={heading}>
          <Title1>Profile</Title1>
        </h1>
        <span className={styles.muted}>
          Manage your agent details. Verified identities and agency information are read-only.
        </span>
      </div>

      {state.status === 'loading' && <Spinner label="Loading your profile..." />}
      {state.status === 'error' && (
        <Card appearance="outline" className={styles.errorCard}>
          <div className={styles.errorContent} role="alert" aria-labelledby="profile-error-title">
            <span className={styles.errorIcon} aria-hidden="true">
              <ErrorCircleRegular />
            </span>
            <Title3 as="h2" id="profile-error-title">Unable to load your profile</Title3>
            <p className={styles.errorDescription}>{state.message}</p>
          </div>
          <Button
            appearance="primary"
            icon={state.unauthenticated ? undefined : <ArrowClockwise20Regular />}
            onClick={() => state.unauthenticated ? void logout() : setAttempt((value) => value + 1)}
          >
            {state.unauthenticated ? 'Sign in again' : 'Try again'}
          </Button>
        </Card>
      )}

      {profile && (
        <>
          <form onSubmit={handleSubmit(saveChanges)} noValidate aria-label="Agent profile">
            <Card appearance="outline" className={styles.card}>
              <div className={styles.cardHeading}>
                <Title3 as="h2">Your details</Title3>
                {!editing && (
                  <Button
                    ref={editButton}
                    icon={<Edit20Regular />}
                    onClick={() => {
                      reset(initialAgentDetails(profile.agent, country));
                      setEditing(true);
                      setSaved(false);
                    }}
                    type="button"
                  >
                    Edit profile
                  </Button>
                )}
              </div>
              <span className={styles.muted}>
                Your verified email identities cannot be changed from the portal.
              </span>
              {saved && <span role="status" className={styles.saved}>Your profile has been updated.</span>}
              {saveError && (
                <div role="alert" className={styles.saveError}>
                  <strong>Changes were not saved</strong>
                  <span>{saveError.message}</span>
                  {saveError.type === 'Unauthenticated' && (
                    <Button type="button" onClick={() => void logout()}>Sign in again</Button>
                  )}
                </div>
              )}
              <div className={styles.grid}>
                {REQUIRED_AGENT_FIELDS.map((field) => editing ? (
                  <Controller
                    key={field}
                    name={field}
                    control={control}
                    render={({ field: inputField, fieldState }) => (
                      <Field
                        label={AGENT_LABELS[field]}
                        required
                        validationState={fieldState.error ? 'error' : 'none'}
                        validationMessage={fieldState.error?.message}
                        hint={field === 'phone'
                          ? phoneFormat === 'us'
                            ? 'Format: (XXX) XXX-XXXX'
                            : 'Include the country code, starting with +.'
                          : undefined}
                      >
                        <Input
                          appearance={saving ? 'filled-darker' : 'outline'}
                          className={saving ? styles.readOnlyControl : undefined}
                          input={{ ref: inputField.ref }}
                          name={inputField.name}
                          onBlur={inputField.onBlur}
                          value={inputField.value}
                          readOnly={saving}
                          maxLength={256}
                          autoFocus={field === 'displayName'}
                          type={field === 'phone' ? 'tel' : 'text'}
                          autoComplete={field === 'displayName' ? 'name' : field === 'phone' ? 'tel' : 'off'}
                          placeholder={field === 'phone'
                            ? phoneFormat === 'us' ? '(555) 123-4567' : '+44 20 7946 0958'
                            : undefined}
                          onChange={(_, data) => inputField.onChange(
                            field === 'phone' ? formatPhoneInput(data.value, country) : data.value,
                          )}
                        />
                      </Field>
                    )}
                  />
                ) : (
                  <ProfileField key={field} label={AGENT_LABELS[field]} value={profile.agent[field]} />
                ))}
                <ProfileField label="Official agency email" value={agencyEmail} identity />
                <ProfileField
                  label="Sign-in email ID"
                  value={userProfile?.mail || account?.username}
                  identity
                />
                {AGENT_ADDRESS_FIELDS.map((field) => (
                  <Controller
                    key={field}
                    name={field}
                    control={control}
                    render={({ field: inputField, fieldState }) => (
                      <AgentAddressField
                        field={field}
                        value={editing ? inputField.value : profile.agent[field] ?? ''}
                        onChange={inputField.onChange}
                        onBlur={inputField.onBlur}
                        inputRef={inputField.ref}
                        error={fieldState.error?.message}
                        readOnly={!editing || saving}
                        className={field === 'street' ? styles.fullWidthField : undefined}
                      />
                    )}
                  />
                ))}
              </div>
              {editing && (
                <div className={styles.actions}>
                  <Button
                    type="button"
                    disabled={saving}
                    onClick={(event) => requestDiscard('cancel', event.currentTarget)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" appearance="primary" disabled={saving || !dirty}>
                    {saving ? 'Saving...' : 'Save changes'}
                  </Button>
                </div>
              )}
              <Dialog
                open={discardAction !== null}
                onOpenChange={(_, data) => { if (!data.open) keepEditing(); }}
                modalType="alert"
              >
                <DialogSurface>
                  <DialogBody>
                    <DialogTitle>Discard changes?</DialogTitle>
                    <DialogContent>
                      Your profile changes have not been saved. Discarding them cannot be undone.
                    </DialogContent>
                    <DialogActions>
                      <Button appearance="secondary" onClick={keepEditing}>Keep editing</Button>
                      <Button
                        appearance="primary"
                        onClick={() => {
                          const action = discardAction;
                          setDiscardAction(null);
                          if (action === 'back') onBack();
                          else finishEditing();
                        }}
                      >
                        Discard changes
                      </Button>
                    </DialogActions>
                  </DialogBody>
                </DialogSurface>
              </Dialog>
            </Card>
          </form>

          <Card appearance="outline" className={styles.card}>
            <Title3 as="h2">Agency details</Title3>
            <span className={styles.muted}>
              Agency information is managed outside the portal and cannot be changed here.
              {' '}If it needs to be updated, email{' '}
              <a href="mailto:usleainfo@microsoft.com">usleainfo@microsoft.com</a>.
            </span>
            {profile.agency ? (
              <div className={styles.grid}>
                <ProfileField label="Agency email domain" value={agencyEmail?.split('@')[1]} identity />
                <ProfileField label="Agency full name" value={profile.agency.name} />
                <ProfileField className={styles.fullWidthField} label="Street address" value={profile.agency.street} />
                <ProfileField label="City" value={profile.agency.city} />
                <ProfileField label="State or province" value={profile.agency.state} />
                <ProfileField label="ZIP or postal code" value={profile.agency.zip} />
                <ProfileField label="Country" value={profile.agency.country} />
                <ProfileField label="Agency phone number" value={profile.agency.phone} />
                <ProfileField label="Jurisdiction or geographic area" value={profile.agency.jurisdiction} />
              </div>
            ) : (
              <span>No agency is registered for your account.</span>
            )}
          </Card>
        </>
      )}
    </section>
  );
}
